import { apiClient } from './api';

export interface FoodFactResponse {
    id: number;
    title: string;
    fact: string;
    source: string;
    sourceUrl?: string; // ✅ NUEVO
    category?: string;
    keywords?: string[];
    relatedRecipes?: any[];
    relatedRecipesCount?: number;
    isActive?: boolean;
}

const PUBLIC_API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Cliente HTTP sin autenticación para endpoints públicos
const publicApiClient = {
    async get(url: string) {
        const response = await fetch(`${PUBLIC_API_BASE_URL}${url}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`❌ Public API Error ${response.status}:`, errorText);
            throw new Error(errorText || 'Request failed');
        }

        return response.json();
    },
};

export const foodFactService = {
    // Obtener todos los food facts (público - sin token)
    async getAllFoodFacts(): Promise<FoodFactResponse[]> {
        try {
            // Primero intentar con el cliente público
            return await publicApiClient.get('/food-facts/active');
        } catch (error: any) {
            console.warn('Public endpoint failed, trying with auth...', error);
            // Si falla, intentar con autenticación (para desarrollo)
            try {
                return await apiClient.get('/food-facts/active');
            } catch (authError) {
                // Si ambos fallan, usar datos mock como fallback
                console.warn('Both endpoints failed, using mock data', authError);
                return getMockFoodFacts();
            }
        }
    },

    // Obtener food facts por categoría
    async getFoodFactsByCategory(category: string): Promise<FoodFactResponse[]> {
        try {
            return await publicApiClient.get(`/food-facts/category/${category}`);
        } catch (error: any) {
            console.warn('Public endpoint failed, using mock data', error);
            return getMockFoodFacts().filter(fact =>
                fact.category === category || category === 'TODOS'
            );
        }
    },

    // Buscar food facts
    async searchFoodFacts(query: string): Promise<FoodFactResponse[]> {
        try {
            return await publicApiClient.get(`/food-facts/search?q=${encodeURIComponent(query)}`);
        } catch (error: any) {
            console.warn('Public endpoint failed, using mock search', error);
            const searchTerm = query.toLowerCase();
            return getMockFoodFacts().filter(fact =>
                fact.title.toLowerCase().includes(searchTerm) ||
                fact.fact.toLowerCase().includes(searchTerm) ||
                fact.keywords?.some(keyword => keyword.toLowerCase().includes(searchTerm))
            );
        }
    },

    // Obtener food facts relevantes para una receta (puede requerir auth)
    async getRelevantFactsForRecipe(recipeId: number): Promise<FoodFactResponse[]> {
        try {
            // Intentar sin autenticación primero
            return await publicApiClient.get(`/food-facts/recipe/${recipeId}`);
        } catch (error: any) {
            console.warn('Recipe facts endpoint failed, using mock data', error);

            // Si falla por 403 o otro error, usar datos mock
            return getMockFoodFacts().slice(0, 1); // Devolver primeros 2 como mock
        }
    },

    // Los demás métodos (CRUD) siguen requiriendo autenticación
    async getFoodFactById(id: number): Promise<FoodFactResponse> {
        return apiClient.get(`/food-facts/${id}`);
    },

    async createFoodFact(foodFactData: any, token: string): Promise<FoodFactResponse> {
        return apiClient.post('/food-facts', foodFactData, token);
    },

    async updateFoodFact(id: number, foodFactData: any, token: string): Promise<FoodFactResponse> {
        return apiClient.put(`/food-facts/${id}`, foodFactData, token);
    },

    async deleteFoodFact(id: number, token: string): Promise<boolean> {
        return apiClient.delete(`/food-facts/${id}`, token);
    }
};

// Datos mock como fallback
function getMockFoodFacts(): FoodFactResponse[] {
    return [
        {
            id: 1,
            title: "Aguacate vs Otros Aceites",
            fact: "El aceite de aguacate mexicano tiene un punto de humo más alto que el aceite de oliva (271°C vs 190°C), siendo ideal para cocinar a altas temperaturas sin perder propiedades.",
            source: "Instituto Nacional de Investigaciones Forestales, Agrícolas y Pecuarias",
            sourceUrl: "https://www.gob.mx/inifap",
            category: "GRASAS_SALUDABLES",
            keywords: ["aguacate", "grasas", "cocina", "mexicano"],
            relatedRecipesCount: 5
        },
        {
            id: 2,
            title: "Poder Nutricional del Chile",
            fact: "Los chiles mexicanos contienen más vitamina C que las naranjas. Un chile habanero tiene 357mg de vitamina C por 100g, mientras que la naranja tiene solo 53mg.",
            source: "Universidad Nacional Autónoma de México - Instituto de Biología",
            sourceUrl: "https://www.ib.unam.mx",
            category: "ANTIOXIDANTES",
            keywords: ["chile", "vitamina c", "antioxidante", "mexicano"],
            relatedRecipesCount: 8
        },
        {
            id: 3,
            title: "Frijoles: Proteína Completa Mexicana",
            fact: "La combinación tradicional mexicana de frijoles con maíz (tortillas) crea una proteína completa con todos los aminoácidos esenciales, equivalente a la carne.",
            source: "Secretaría de Agricultura y Desarrollo Rural",
            sourceUrl: "https://www.gob.mx/agricultura",
            category: "PROTEINAS",
            keywords: ["frijol", "proteína", "maíz", "tradicional"],
            relatedRecipesCount: 12
        },
        {
            id: 4,
            title: "Grasas Saludables del Aguacate",
            fact: "El aguacate contiene grasas monoinsaturadas que ayudan a reducir el colesterol LDL, es rico en potasio y fibra.",
            source: "Avocado Nutrition Center",
            category: "GRASAS_SALUDABLES",
            keywords: ["aguacate", "grasas", "potasio", "fibra"],
            relatedRecipesCount: 6
        },
        {
            id: 5,
            title: "Cena Nutritiva y Ligera",
            fact: "Cenar ligero pero nutritivo ayuda a una mejor digestión, calidad de sueño y mantiene el metabolismo activo durante la noche.",
            source: "Sociedad Española de Nutrición",
            category: "CENA",
            keywords: ["cena", "ligero", "proteína", "sueño"],
            relatedRecipesCount: 7
        },
        {
            id: 6,
            title: "Snacks Inteligentes",
            fact: "Los snacks entre comidas mantienen el metabolismo activo, evitan atracones y proporcionan nutrientes esenciales.",
            source: "Harvard Nutrition Source",
            category: "SNACK",
            keywords: ["snack", "metabolismo", "proteína", "fibra"],
            relatedRecipesCount: 4
        }
    ];
}